import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { useGetClientNetworkQuery } from "../services/rtkQueryServices/checkApiService";
import { themeFor } from "./themes";
import { ThemeModeContext } from "./ThemeContext";

const DEV_THEME_KEY = "raise_dev_theme";

// DEV ONLY: lets you preview either palette without the backend.
//   /?theme=uta    -> force UTA brand    /?theme=raise  -> force RAISE neutral
//   /?theme=off    -> clear the override and go back to the real network check
// The choice is remembered (localStorage) so it survives client-side navigation.
// Disabled entirely in production builds, so it can never affect real visitors.
function useDevThemeOverride() {
  const { search } = useLocation();

  if (process.env.NODE_ENV !== "development") return null;

  const q = new URLSearchParams(search).get("theme");
  try {
    if (q === "off" || q === "clear") {
      localStorage.removeItem(DEV_THEME_KEY);
      return null;
    }
    if (q === "uta" || q === "raise") {
      localStorage.setItem(DEV_THEME_KEY, q);
      return q;
    }
    const stored = localStorage.getItem(DEV_THEME_KEY);
    if (stored === "uta" || stored === "raise") return stored;
  } catch (e) {
    /* localStorage unavailable - ignore */
  }
  return null;
}

// Single source of the network verdict for the whole app. Fetches GET /api/client-network
// once (RTK Query dedupes with NetworkLandingRedirect's call), then drives BOTH the MUI
// theme and the [data-theme] attribute on <html> that the SCSS tokens key off - so every
// page renders in one palette: UTA for UTA-network visitors, RAISE (neutral) for everyone else.
export default function ThemeController({ children }) {
  const { data, isLoading } = useGetClientNetworkQuery();
  const override = useDevThemeOverride();

  const isResolved = !isLoading;
  // Default to the neutral RAISE theme until the check resolves; only switch to UTA once confirmed.
  const networkIsUta = isResolved && Boolean(data?.is_uta_network);
  // A dev override (if any) wins; otherwise the real network verdict decides.
  const isUta = override ? override === "uta" : networkIsUta;

  useEffect(() => {
    document.documentElement.dataset.theme = isUta ? "uta" : "raise";
  }, [isUta]);

  const theme = useMemo(() => themeFor(isUta), [isUta]);
  const mode = useMemo(
    () => ({ isUta, isResolved: isResolved || Boolean(override) }),
    [isUta, isResolved, override]
  );

  return (
    <ThemeModeContext.Provider value={mode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
